<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">

    <!-- Mobile skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="flex flex-col items-center pt-8 pb-6 px-4">
          <div class="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div class="mt-3 h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="mt-1.5 h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div class="mt-3 h-7 w-28 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
        </div>
        <div class="px-4 pt-4 pb-1.5 h-3 w-14 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
          <div v-for="i in 3" :key="i" class="px-4 py-3 space-y-1.5">
            <div class="h-2.5 bg-gray-200 dark:bg-gray-700 rounded" :style="{ width: i === 1 ? '5rem' : i === 2 ? '5.5rem' : '5.5rem' }"></div>
            <div class="h-5 w-full bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
        <div class="px-4 pt-5 pb-1.5 h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-3 space-y-1.5">
            <div class="h-2.5 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-20 w-full bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
        <div class="px-4 pt-5 pb-1.5 h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
          <div v-for="i in 2" :key="i" class="px-4 py-3.5 flex items-center justify-between gap-4">
            <div class="flex-1 space-y-1.5">
              <div class="h-3 w-44 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div class="h-2.5 w-36 bg-gray-100 dark:bg-gray-800 rounded"></div>
            </div>
            <div class="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile loaded content -->
    <template v-else>

      <!-- Cover banner -->
      <div class="h-28 bg-gradient-to-br from-blue-500 to-blue-700 relative overflow-hidden">
        <div class="absolute inset-0 opacity-20"
             style="background-image: radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px); background-size: 24px 24px;"></div>
      </div>

      <!-- Profile photo card -->
      <div class="bg-white dark:bg-[#1C2128] mx-4 -mt-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 px-4 pb-5">
        <div class="flex flex-col items-center">
          <!-- Avatar overlapping banner -->
          <div class="relative -mt-10">
            <AvatarUploadCard
              ref="mobileAvatarCard"
              :picture-url="useSettingsStoreObj().current_user.profile_picture_url"
              :avatar-name="useSettingsStoreObj().current_user.avatar_name"
              :upload-url="'/api/v1/users/profile_picture'"
              :remove-url="'/api/v1/users/remove_profile_picture'"
              form-field-name="user[profile_picture]"
              title="Change profile picture"
              :locked="useSettingsStoreObj().current_user.verified"
              locked-message="Your profile is verified and cannot be changed"
              @updated="onAvatarUpdated"
              @error="onAvatarError"
            />
            <!-- Verified badge -->
            <svg v-if="useSettingsStoreObj().current_user.verified"
                 class="absolute bottom-0 right-0 w-6 h-6 drop-shadow-sm"
                 viewBox="0 0 24 24" fill="none">
              <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
              <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>

          <p class="mt-3 text-base font-bold text-gray-900 dark:text-white leading-snug">
            {{ useSettingsStoreObj().current_user.full_name || 'Your name' }}
          </p>
          <p class="text-sm text-gray-400 dark:text-gray-500 mt-0.5">
            {{ useSettingsStoreObj().current_user.designation || 'Add a designation' }}
          </p>
          <button v-if="!useSettingsStoreObj().current_user.verified"
                  @click="$refs.mobileAvatarCard.openModal()" type="button"
                  class="mt-3 px-5 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full">
            Change Photo
          </button>
          <p v-else class="mt-3 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <svg class="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none">
              <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
              <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Verified. Photo cannot be changed.
          </p>
        </div>
      </div>

      <div class="pb-28 mt-5">

        <!-- Get Verified card (mobile) -->
        <template v-if="!useSettingsStoreObj().current_user.verified">
          <p class="px-4 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Profile Verification</p>
          <router-link to="/settings/profile/verification" data-turbo-prefetch="false"
            class="mx-4 block bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden mb-6">
            <div class="px-4 py-4 flex items-start gap-3">
              <div class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none">
                  <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                  <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-white leading-snug">Get Verified</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-relaxed">Add a verified badge to your profile. Build trust and increase booking confidence.</p>
              </div>
              <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
              </svg>
            </div>
          </router-link>
        </template>
        <!-- Identity -->
        <p class="px-4 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Identity</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
          <div class="px-4 py-3" :title="useSettingsStoreObj().current_user.verified ? 'Your profile is verified and cannot be changed' : ''">
            <label class="block text-[10px] font-semibold uppercase tracking-wider mb-1"
                   :class="useSettingsStoreObj().current_user.verified ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'">
              Full Name
              <span v-if="useSettingsStoreObj().current_user.verified" class="ml-1 normal-case font-normal text-gray-300 dark:text-gray-600">— verified, locked</span>
            </label>
            <input :value="useSettingsStoreObj().current_user.full_name"
                   @focusout="updateProfilePageData"
                   name="user[full_name]" type="text"
                   :placeholder="I18N.application.onboarding.full_name"
                   :disabled="useSettingsStoreObj().current_user.verified"
                   :class="useSettingsStoreObj().current_user.verified ? 'opacity-50 cursor-not-allowed select-none' : ''"
                   class="w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none placeholder-gray-300 dark:placeholder-gray-600 disabled:pointer-events-none" />
          </div>
          <div class="px-4 py-3">
            <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Designation</label>
            <input :value="useSettingsStoreObj().current_user.designation"
                   @focusout="updateProfilePageData"
                   name="user[designation]" type="text"
                   :placeholder="I18N.website_content.account_settings.designation"
                   class="w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none placeholder-gray-300 dark:placeholder-gray-600" />
          </div>
          <div class="px-4 py-3">
            <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Profile URL</label>
            <div class="flex items-center gap-1">
              <span class="text-xs text-gray-400 dark:text-gray-500 font-mono flex-shrink-0">{{ APPLICATION_DOMAIN }}/</span>
              <input :value="user_url"
                     @input="validateURL"
                     @focusout="updateProfilePageData"
                     name="user[url]" type="text" placeholder="your-url"
                     class="flex-1 text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none font-mono placeholder-gray-300 dark:placeholder-gray-600 min-w-0" />
            </div>
            <p v-if="errors.url" class="mt-1 text-xs text-red-500">{{ errors.url }}</p>
            <a v-else-if="user_url" :href="'/' + user_url" target="_blank"
               class="inline-flex items-center gap-1 mt-1 text-[11px] text-blue-500 dark:text-blue-400 font-mono">
              /{{ user_url }}
              <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- About -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">About</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-3">
            <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Bio</label>
            <textarea :value="useSettingsStoreObj().current_user.bio"
                      @focusout="updateProfilePageData"
                      name="user[bio]" rows="4"
                      :placeholder="I18N.website_content.account_settings.profile_tab.bio"
                      class="w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none resize-none placeholder-gray-300 dark:placeholder-gray-600 leading-relaxed"></textarea>
          </div>
        </div>

        <!-- Preferences -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Preferences</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
          <div class="px-4 py-3.5 flex items-center justify-between gap-4">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">{{ I18N.website_content.account_settings.show_msg_btn_on_profile_page }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.subheading1 }}</p>
            </div>
            <ToggleSwitch
              :model-value="useSettingsStoreObj().current_user.show_message_button_on_profile"
              @change="togglePreference('show_message_button_on_profile', $event)"
            />
          </div>
          <div class="px-4 py-3.5 flex items-center justify-between gap-4">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">{{ I18N.website_content.account_settings.show_msg_btn_on_events_page }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.subheading2 }}</p>
            </div>
            <ToggleSwitch
              :model-value="useSettingsStoreObj().current_user.show_message_button_on_events"
              @change="togglePreference('show_message_button_on_events', $event)"
            />
          </div>
        </div>
      </div>
    </template>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
          <div class="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
            <div class="h-6 w-16 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6">
          <div class="flex flex-col lg:flex-row gap-5">
            <div class="flex-1 min-w-0 space-y-4">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-md"></div>
                  <div class="space-y-1.5">
                    <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div class="h-2.5 w-28 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                </div>
                <div class="px-6 py-5 space-y-4">
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                      <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                    </div>
                    <div>
                      <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                      <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                    </div>
                  </div>
                  <div>
                    <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                    <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                  </div>
                </div>
              </div>
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-md"></div>
                  <div class="space-y-1.5">
                    <div class="h-3 w-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div class="h-2.5 w-36 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                </div>
                <div class="px-6 py-5 space-y-2">
                  <div class="h-2.5 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div class="h-24 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                </div>
              </div>
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-md"></div>
                  <div class="space-y-1.5">
                    <div class="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div class="h-2.5 w-40 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                </div>
                <div v-for="i in 2" :key="i"
                     class="px-6 py-4 flex items-center justify-between gap-6 border-b border-gray-50 dark:border-gray-800 last:border-0">
                  <div class="space-y-1.5 flex-1">
                    <div class="h-3.5 w-44 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div class="h-3 w-56 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                  <div class="h-6 w-11 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
                </div>
              </div>
            </div>
            <div class="w-full lg:w-64 flex-shrink-0 order-first lg:order-last">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="h-16 bg-gray-200 dark:bg-gray-700 rounded-t-xl"></div>
                <div class="px-6 pb-5">
                  <div class="flex flex-col items-center -mt-10">
                    <div class="w-20 h-20 rounded-full bg-gray-300 dark:bg-gray-600 ring-4 ring-white dark:ring-[#1C2128]"></div>
                    <div class="mt-3 space-y-1.5 text-center">
                      <div class="h-3.5 w-28 bg-gray-200 dark:bg-gray-700 rounded mx-auto"></div>
                      <div class="h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded mx-auto"></div>
                      <div class="h-3 w-16 bg-gray-100 dark:bg-gray-800 rounded mx-auto"></div>
                    </div>
                    <div class="w-full mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <div class="h-8 w-full bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Desktop loaded content -->
    <template v-else>
      <!-- Sub-page header -->
      <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
        <div class="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
          <div class="flex items-center gap-2.5">
            <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
              </svg>
            </div>
            <div>
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Public Profile</h1>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">General settings</p>
            </div>
          </div>
          <SaveStatusPill :status="saveStatus" />
        </div>
      </div>

      <!-- Page content -->
      <div class="max-w-5xl mx-auto px-6 py-6">
        <div class="flex flex-col lg:flex-row gap-5">

          <!-- Left column -->
          <div class="flex-1 min-w-0 space-y-4">

            <!-- Identity card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Identity</h2>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Your public-facing name and profile URL</p>
                </div>
              </div>
              <div class="px-6 py-5 space-y-4">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label for="user_full_name" class="block text-xs font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1.5"
                           :class="useSettingsStoreObj().current_user.verified ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'">
                      Full name
                      <span v-if="useSettingsStoreObj().current_user.verified"
                            class="inline-flex items-center gap-1 text-[10px] font-medium text-blue-500 dark:text-blue-400 normal-case tracking-normal">
                        <svg class="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                          <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                          <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Verified. Name cannot be changed.
                      </span>
                    </label>
                    <input :value="useSettingsStoreObj().current_user.full_name"
                           @focusout="updateProfilePageData"
                           name="user[full_name]" id="user_full_name" type="text"
                           :placeholder="I18N.application.onboarding.full_name"
                           :disabled="useSettingsStoreObj().current_user.verified"
                           :title="useSettingsStoreObj().current_user.verified ? 'Your profile is verified and cannot be changed' : ''"
                           :class="useSettingsStoreObj().current_user.verified ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50' : 'bg-white dark:bg-[#0d1117]'"
                           class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:pointer-events-none" />
                  </div>
                  <div>
                    <label for="user_designation" class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Designation</label>
                    <input :value="useSettingsStoreObj().current_user.designation"
                           @focusout="updateProfilePageData"
                           name="user[designation]" id="user_designation" type="text"
                           :placeholder="I18N.website_content.account_settings.designation"
                           class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                  </div>
                </div>
                <div>
                  <label for="user_url" class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Profile URL</label>
                  <div class="flex rounded-lg border transition-all"
                       :class="errors.url
                         ? 'border-red-400 dark:border-red-500 ring-2 ring-red-400 dark:ring-red-500'
                         : 'border-gray-200 dark:border-gray-700 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent'">
                    <span class="inline-flex items-center pl-3.5 pr-3 bg-gray-50 dark:bg-[#1C2128] border-r border-gray-200 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500 select-none flex-shrink-0 rounded-l-lg font-mono">
                      {{ APPLICATION_DOMAIN }}/
                    </span>
                    <input :value="user_url"
                           @input="validateURL"
                           @focusout="updateProfilePageData"
                           name="user[url]" id="user_url" type="text" placeholder="your-url"
                           class="flex-1 px-3 py-2.5 text-sm bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 border-0 focus:outline-none focus:ring-0 rounded-r-lg font-mono" />
                  </div>
                  <div class="mt-1.5 flex items-center gap-1.5">
                    <template v-if="errors.url">
                      <svg class="w-3.5 h-3.5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
                      </svg>
                      <p class="text-xs text-red-500">{{ errors.url }}</p>
                    </template>
                    <template v-else>
                      <svg class="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
                      </svg>
                      <a :href="'/' + user_url" target="_blank"
                         class="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 hover:underline truncate font-mono">
                        {{ APPLICATION_URL }}/{{ user_url }}
                      </a>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- About card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">About</h2>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Shown on your public profile page</p>
                </div>
              </div>
              <div class="px-6 py-5">
                <label for="user_bio" class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Bio</label>
                <textarea :value="useSettingsStoreObj().current_user.bio"
                          @focusout="updateProfilePageData"
                          @input="e => bio_length = e.target.value.length"
                          name="user[bio]" id="user_bio" rows="4"
                          :placeholder="I18N.website_content.account_settings.profile_tab.bio"
                          class="w-full px-3.5 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none leading-relaxed"></textarea>
                <div class="mt-1.5 flex items-center justify-between">
                  <p class="text-xs text-gray-400 dark:text-gray-500">A short intro visitors see before booking with you.</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{{ bio_length }} / 300</p>
                </div>
              </div>
            </div>

            <!-- Preferences card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 0 1 1.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.559.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.894.149c-.424.07-.764.383-.929.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 0 1-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.398.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 0 1-.12-1.45l.527-.737c.25-.35.272-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 0 1 .12-1.45l.773-.773a1.125 1.125 0 0 1 1.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894Z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Preferences</h2>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Control what visitors see on your pages</p>
                </div>
              </div>
              <div class="px-6 py-4 flex items-center justify-between gap-6 border-b border-gray-50 dark:border-gray-800/60">
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">{{ I18N.website_content.account_settings.show_msg_btn_on_profile_page }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.subheading1 }}</p>
                </div>
                <ToggleSwitch
                  :model-value="useSettingsStoreObj().current_user.show_message_button_on_profile"
                  @change="togglePreference('show_message_button_on_profile', $event)"
                />
              </div>
              <div class="px-6 py-4 flex items-center justify-between gap-6">
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">{{ I18N.website_content.account_settings.show_msg_btn_on_events_page }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.subheading2 }}</p>
                </div>
                <ToggleSwitch
                  :model-value="useSettingsStoreObj().current_user.show_message_button_on_events"
                  @change="togglePreference('show_message_button_on_events', $event)"
                />
              </div>
            </div>
          </div>

          <!-- Right column: profile photo card + get verified -->
          <div class="w-full lg:w-64 flex-shrink-0 order-first lg:order-last space-y-4">
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="h-16 bg-gradient-to-br from-blue-500 to-blue-700 relative rounded-t-xl overflow-hidden">
                <div class="absolute inset-0 opacity-20"
                     style="background-image: radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px); background-size: 24px 24px;"></div>
              </div>
              <div class="px-6 pb-5">
                <div class="flex flex-col items-center -mt-10">
                  <div class="relative">
                    <AvatarUploadCard
                      ref="desktopAvatarCard"
                      :picture-url="useSettingsStoreObj().current_user.profile_picture_url"
                      :avatar-name="useSettingsStoreObj().current_user.avatar_name"
                      :upload-url="'/api/v1/users/profile_picture'"
                      :remove-url="'/api/v1/users/remove_profile_picture'"
                      form-field-name="user[profile_picture]"
                      title="Change profile picture"
                      :locked="useSettingsStoreObj().current_user.verified"
                      locked-message="Your profile is verified and cannot be changed"
                      @updated="onAvatarUpdated"
                      @error="onAvatarError"
                    />
                    <!-- Verified badge -->
                    <svg v-if="useSettingsStoreObj().current_user.verified"
                         class="absolute bottom-0 right-0 w-6 h-6 drop-shadow-sm"
                         viewBox="0 0 24 24" fill="none">
                      <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                      <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div class="text-center mt-3">
                    <p class="text-sm font-bold text-gray-900 dark:text-white leading-snug">{{ useSettingsStoreObj().current_user.full_name || 'Your name' }}</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ useSettingsStoreObj().current_user.designation || 'Add a designation' }}</p>
                    <a v-if="user_url" :href="'/' + user_url" target="_blank"
                       class="inline-flex items-center gap-1 mt-1.5 text-[11px] text-blue-500 dark:text-blue-400 hover:underline font-mono">
                      /{{ user_url }}
                      <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                      </svg>
                    </a>
                  </div>
                  <div class="w-full mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2">
                    <button v-if="!useSettingsStoreObj().current_user.verified"
                            type="button" @click="$refs.desktopAvatarCard.openModal()"
                            class="w-full inline-flex items-center justify-center gap-2 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"/>
                      </svg>
                      Change photo
                    </button>
                    <p v-else class="text-center text-[11px] text-gray-400 dark:text-gray-500 flex items-center justify-center gap-1">
                      <svg class="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                        <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                        <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      Verified. Photo cannot be changed.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Get Verified card (desktop) -->
            <router-link v-if="!useSettingsStoreObj().current_user.verified"
              to="/settings/profile/verification" data-turbo-prefetch="false"
              class="block bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:border-blue-300 dark:hover:border-blue-600 transition-colors group">
              <div class="px-4 py-4 flex items-start gap-3">
                <div class="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                  <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                    <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-gray-900 dark:text-white leading-snug group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Get Verified</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-relaxed">Add a verified badge to your profile. Build trust and increase booking confidence.</p>
                </div>
                <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
                </svg>
              </div>
            </router-link>
          </div>

        </div>
      </div>
    </template>
  </div>

</template>

<script>
import { useSettingsStore } from 'controllers/vue_components/settings/v1/store'
import RequestMixin from 'controllers/vue_components/mixins/request'
import Constants from 'controllers/vue_components/mixins/constants'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import ToggleSwitch from 'controllers/vue_components/settings/shared/ToggleSwitch'
import SaveStatusPill from 'controllers/vue_components/settings/shared/SaveStatusPill'
import AvatarUploadCard from 'controllers/vue_components/settings/shared/AvatarUploadCard'

export default {
    name: 'Profile',
    props: [],
    mixins: [Constants, RequestMixin],
    components: { FlashNotifications, ToggleSwitch, SaveStatusPill, AvatarUploadCard },

    data() {
        return {
            user: {},
            errors: {},
            user_url: '',
            saveStatus: 'idle',
            bio_length: 0,
        }
    },

    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },

        async onAvatarUpdated() {
            await this.useSettingsStoreObj().fetchCurrentUser()
        },

        onAvatarError(message) {
            this.$refs.flash.show(message, 'alert')
        },

        loadData() {
            this.user_url = this.useSettingsStoreObj().current_user.url
            this.bio_length = this.useSettingsStoreObj().current_user.bio?.length || 0
        },

        handleNotifications(response) {
            if (response.js_error === false) {
                if (response.response.success) {
                    this.$refs.flash.show('Profile settings saved', 'notice')
                } else {
                    this.$refs.flash.show(response.response.message, 'alert')
                }
            } else {
                this.$refs.flash.show(response.error?.data?.message || 'Something went wrong', 'alert')
            }
        },

        async markSaved() {
            this.saveStatus = 'saved'
            setTimeout(() => { if (this.saveStatus === 'saved') this.saveStatus = 'idle' }, 2500)
        },

        async updateProfilePageData(e) {
            const [objectName, fieldName] = e.target.name.split('[').map(p => p.replace(']', ''))
            let fieldValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value

            if (objectName === 'user' && this.useSettingsStoreObj().current_user[fieldName] !== fieldValue) {
                this.saveStatus = 'saving'
                const response = await this.useSettingsStoreObj().updateCurrentUser({ user: { [fieldName]: fieldValue } })
                await this.markSaved()
                this.handleNotifications(response)
            }
            if (objectName === 'social_library' && this.useSettingsStoreObj().current_user.social_library?.[fieldName] !== fieldValue) {
                this.saveStatus = 'saving'
                const response = await this.useSettingsStoreObj().updateCurrentUserSocialLinks({ social_library: { [fieldName]: fieldValue } })
                await this.markSaved()
                this.handleNotifications(response)
            }
        },

        async togglePreference(fieldName, newValue) {
            this.saveStatus = 'saving'
            const response = await this.useSettingsStoreObj().updateCurrentUser({ user: { [fieldName]: newValue } })
            await this.markSaved()
            this.handleNotifications(response)
        },

        validateURL(e) {
            this.user_url = e.target.value
            clearTimeout(this._urlDebounceTimer)
            this._urlDebounceTimer = setTimeout(async () => {
                const url = this.user_url
                if (!url) {
                    this.errors.url = this.I18N.validations.url_cant_be_blank
                } else {
                    delete this.errors.url
                    const res = await this.axiosRequest('POST', '/api/v1/users/validate_url_availability', { user: { url } }, false)
                    if (res.js_error === false && res.response.success) {
                        if (res.response.data.available === false) {
                            this.errors.url = res.response.message
                        }
                    }
                }
            }, 400)
        },
    },

    computed: {},

    watch: {
        user_url(newValue) {
            if (newValue == null) return
            this.user_url = newValue.replace(/\s+/g, '-').toLowerCase()
        },
    },

    created() {
        this.$watch(
            () => this.useSettingsStoreObj().init_data_loaded,
            (loaded) => { if (loaded) this.loadData() },
            { immediate: true }
        )
    },
}
</script>
